import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

import Logout from 'components/logout';
import { useAuth } from 'context/AuthContext';
import DashboardLayout from 'layouts/DashboardLayout';
import AppLoader from 'components/AppLoader';
import PlayerManagement from 'pages/PlayerManagement';

const AgDashboard = lazy(() => import('pages/AgDashboard'));
const AgEarnings = lazy(() => import('pages/AgEarnings'));
const AgGuidelines = lazy(() => import('pages/AgGuidelines'));
const AgUsers = lazy(() => import('pages/AgUsers'));
const AgUserDetails = lazy(() => import('pages/AgUserDetails'));
const AgWallet = lazy(() => import('pages/AgWallet'));
const AgentManagement = lazy(() => import('pages/AgentManagement'));
const Banners = lazy(() => import('pages/Banner'));
const Dashboard = lazy(() => import('pages/Dashboard'));
const EditUser = lazy(() => import('pages/EditUser'));
const ForgotPasswordPage = lazy(() => import('pages/ForgotPassword'));
const GameList = lazy(() => import('pages/GameList'));
const Guidelines = lazy(() => import('pages/Guidelines'));
const IssueManagement = lazy(() => import('pages/IssueManagement'));
const LoginPage = lazy(() => import('pages/Login'));
const Settings = lazy(() => import('pages/Settings'));
const UserManagement = lazy(() => import('pages/UserManagement'));
const WalletManagement = lazy(() => import('pages/WalletManagement'));
const AppVersion = lazy(() => import('pages/AppVersion'));
const AgUserTransaction = lazy(() => import('pages/AgUserTransaction'));
const AgTransactionHistory = lazy(() => import('pages/AgTransactionHistory'));
const PaymentMethods = lazy(() => import('pages/PaymentMethods'));
const PaymentTickets = lazy(() => import('pages/PaymentTickets'));
const KycUploads = lazy(() => import('pages/KycUploads'));
const PayoutReview = lazy(() => import('pages/PayoutReview'));

function App() {
  const { isLoggedIn, isAuthenticating, user } = useAuth();
  const location = useLocation();

  if (isAuthenticating)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={90} />
      </div>
    );

  return (
    <React.Fragment>
      <Suspense fallback={<AppLoader isLoading />}>
        <Routes>
          {isLoggedIn ? (
            user.role === 'agent' ? (
              <Route path='/' element={<DashboardLayout />}>
                <Route path='/' element={<Navigate to='/agent/dashboard' replace />} />
                <Route path='settings' element={<Settings />} />
                <Route path='logout' element={<Logout />} />
                <Route path='/agent/dashboard' element={<AgDashboard />} />
                <Route path='/agent/withdrawal-request' element={<AgWallet />} />
                <Route path='/agent/earnings' element={<AgEarnings />} />
                <Route path='/agent/settings' element={<Settings />} />
                <Route path='/agent/players' element={<AgUsers />} />
                <Route path='/agent/players/:id' element={<AgUserDetails />} />
                <Route path='/agent/guideline' element={<AgGuidelines />} />
                <Route path='/agent/players-transaction' element={<AgUserTransaction />} />
                <Route path='/agent/agent-transaction' element={<AgTransactionHistory />} />
                <Route path='/agent/payment-tickets' element={<PaymentTickets />} />
                <Route path='*' index element={<Navigate to='/agent/dashboard' replace />} />
              </Route>
            ) : (
              <Route path='/' element={<DashboardLayout />}>
                <Route path='/' element={<Navigate to='/dashboard' replace />} />
                <Route path='dashboard' element={<Dashboard />} />
                <Route path='player-management' element={<PlayerManagement />} />
                <Route path='player-management/:id' element={<AgUserDetails />} />
                <Route path='user-management' element={<UserManagement />} />
                <Route path='agent-management' element={<AgentManagement />} />
                <Route path='wallet-information' element={<WalletManagement />} />
                <Route path='kyc' element={<KycUploads />} />
                <Route path='issues-management' element={<IssueManagement />} />
                <Route path='guideline' element={<Guidelines />} />
                <Route path='banners' element={<Banners />} />
                <Route path='game-list' element={<GameList />} />
                <Route path='payment-methods' element={<PaymentMethods />} />
                <Route path='users/:id' element={<EditUser />} />
                <Route path='settings' element={<Settings />} />
                <Route path='logout' element={<Logout />} />
                <Route path='app-version' element={<AppVersion />} />
                <Route path='payout-review' element={<PayoutReview />} />
                <Route path='*' element={<Navigate to='/dashboard' replace />} />
              </Route>
            )
          ) : (
            <Route>
              <Route path='/login' element={<LoginPage />} />
              <Route path='/forgot-password' element={<ForgotPasswordPage />} />
              <Route path='/register' element={<></>} />
              <Route path='*' element={<Navigate to='/login' replace />} />
            </Route>
          )}
        </Routes>
      </Suspense>
    </React.Fragment>
  );
}

export default App;
